import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  LinearProgress,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  InsertDriveFile,
  Description,
  Engineering,
  Architecture,
  Map,
  CloudUpload,
  Delete,
} from '@mui/icons-material';
import DocumentUpload from './DocumentUpload';
import { projectsApi } from '../services/api';

interface Document {
  id: string;
  name: string;
  size: number;
  uploaded_at: string;
  status: string;
  ai_processed: boolean;
}

interface SubmissionDocumentsProps {
  projectId: string;
  submissionDocuments: Document[];
  onUploadComplete: () => void;
  loading: boolean;
  onNextToReview?: () => void;
}

interface ComplianceImpact {
  document_id: string;
  document_name: string;
  evaluation_count: number;
  sample_requirement_titles: string[];
  results_to_delete: number;
  results_to_update: number;
  has_compliance_evaluations: boolean;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getSubmissionFileIcon = (filename: string | undefined) => {
  if (!filename) {
    return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }

  const extension = filename.split('.').pop()?.toLowerCase();
  const name = filename.toLowerCase();

  // Check for specific submission document types
  if (name.includes('plan') || name.includes('drawing') || extension === 'dwg') {
    return <Architecture sx={{ color: 'primary.main' }} />;
  }
  if (name.includes('survey') || name.includes('site') || name.includes('boundary')) {
    return <Map sx={{ color: 'success.main' }} />;
  }
  if (name.includes('calculation') || name.includes('calc') || name.includes('analysis')) {
    return <Engineering sx={{ color: 'warning.main' }} />;
  }

  switch (extension) {
    case 'pdf':
      return <Description sx={{ color: 'secondary.main' }} />;
    case 'doc':
    case 'docx':
      return <InsertDriveFile sx={{ color: 'primary.main' }} />;
    case 'dwg':
    case 'dxf':
      return <Architecture sx={{ color: 'primary.main' }} />;
    default:
      return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }
};

export default function SubmissionDocuments({
  projectId,
  submissionDocuments,
  onUploadComplete,
  loading,
  onNextToReview
}: SubmissionDocumentsProps) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingDocuments, setDeletingDocuments] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const handleUploadStart = () => {
    setIsUploading(true);
    setUploadProgress(0);
  };

  const handleUploadProgress = (progress: number) => {
    setUploadProgress(progress);
  };

  const handleUploadFinish = () => {
    setIsUploading(false);
    setUploadProgress(0);
    onUploadComplete();
  };

  const handleDeleteSubmissionDocument = async (documentId: string, documentName: string) => {
    try {
      setError(null);
      setDeletingDocuments(prev => new Set(Array.from(prev).concat(documentId)));

      // First, get the compliance impact for this document
      const complianceImpact: ComplianceImpact = await projectsApi.getSubmissionDocumentComplianceImpact(projectId, documentId);

      let confirmMessage: string;

      if (!complianceImpact.has_compliance_evaluations) {
        // Simple confirmation for documents without compliance evaluations
        confirmMessage = `Are you sure you want to delete "${documentName}"?\n\nThis will permanently delete the file. This action cannot be undone.`;
      } else {
        // Enhanced warning for documents with compliance evaluations
        const sampleList = complianceImpact.sample_requirement_titles.length > 0
          ? `\n\nSome requirements with evaluations:\n• ${complianceImpact.sample_requirement_titles.join('\n• ')}${complianceImpact.evaluation_count > 3 ? `\n• ... and ${complianceImpact.evaluation_count - 3} more` : ''}`
          : '';

        const deletionDetails = [];
        if (complianceImpact.results_to_delete > 0) {
          deletionDetails.push(`${complianceImpact.results_to_delete} complete evaluation result${complianceImpact.results_to_delete !== 1 ? 's' : ''}`);
        }
        if (complianceImpact.results_to_update > 0) {
          deletionDetails.push(`${complianceImpact.results_to_update} evaluation result${complianceImpact.results_to_update !== 1 ? 's' : ''} will be updated`);
        }

        confirmMessage = `⚠️ WARNING: Delete submission document with compliance evaluations?\n\n"${documentName}" has been analyzed for compliance and has ${complianceImpact.evaluation_count} evaluation result${complianceImpact.evaluation_count !== 1 ? 's' : ''}.\n\nDeleting this document will PERMANENTLY:\n• Remove the document file\n• ${deletionDetails.join(' and ')}${sampleList}\n\nThis action cannot be undone.\n\nAre you sure you want to proceed?`;
      }

      if (window.confirm(confirmMessage)) {
        const deleteResult = await projectsApi.deleteSubmissionDocument(projectId, documentId);

        // Refresh the document list
        onUploadComplete();

        console.log(`Successfully deleted submission document: ${documentName}`);
        console.log(`Compliance impact: ${deleteResult.compliance_results_deleted} results deleted, ${deleteResult.compliance_results_updated} results updated`);
      }
    } catch (err) {
      console.error('Error deleting submission document:', err);
      setError(`Failed to delete "${documentName}". Please try again.`);
    } finally {
      setDeletingDocuments(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Submission Documents
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Upload project plans, drawings, site surveys, calculations, and other submission documents that need to comply with the extracted requirements.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Common document types guidance */}
      <Card elevation={0} sx={{ border: 1, borderColor: 'grey.300', p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Common Submission Document Types:
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Architecture sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Site Plans & Drawings</Typography>
              <Typography variant="caption" color="text.secondary">CAD files, architectural drawings</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Map sx={{ color: 'success.main' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Survey Documents</Typography>
              <Typography variant="caption" color="text.secondary">Boundary surveys, topographic maps</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Engineering sx={{ color: 'warning.main' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Calculations & Analysis</Typography>
              <Typography variant="caption" color="text.secondary">Engineering calculations, reports</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Description sx={{ color: 'error.main' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Supporting Documents</Typography>
              <Typography variant="caption" color="text.secondary">Reports, specifications, permits</Typography>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Upload progress */}
      {isUploading && (
        <Card elevation={0} sx={{ border: 1, borderColor: 'info.main', p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CloudUpload sx={{ color: 'info.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Uploading submission documents...
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {uploadProgress}% complete
          </Typography>
        </Card>
      )}

      <DocumentUpload
        projectId={projectId}
        onUploadComplete={handleUploadFinish}
        onUploadStart={handleUploadStart}
        onUploadProgress={handleUploadProgress}
        documentType="submission"
        title="Upload Submission Documents"
        description="Drag and drop your project plans, drawings, surveys, calculations and other submission documents here, or click to browse."
        showHelperText={false}
      />

      {(submissionDocuments.length > 0 || loading) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Uploaded Submission Documents
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : (
            <Card elevation={0} sx={{ border: 1, borderColor: 'grey.300' }}>
              <List>
                {submissionDocuments.map((doc, index) => (
                  <ListItem
                    key={doc.id}
                    divider={index < submissionDocuments.length - 1}
                  >
                    <ListItemIcon>
                      {getSubmissionFileIcon(doc.name)}
                    </ListItemIcon>
                    <ListItemText
                      primary={doc.name}
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(doc.size)} • Uploaded {formatDate(doc.uploaded_at)}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={doc.status === 'uploaded' ? 'Ready for Review' : doc.status}
                              size="small"
                              color={doc.status === 'uploaded' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSubmissionDocument(doc.id, doc.name)}
                        disabled={deletingDocuments.has(doc.id)}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.50'
                          }
                        }}
                        title={`Delete "${doc.name}"`}
                      >
                        {deletingDocuments.has(doc.id) ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Delete sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Card>
          )}
        </Box>
      )}

      {submissionDocuments.length > 0 && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onNextToReview}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Next: Review Compliance ({submissionDocuments.length} documents)
          </Button>
        </Box>
      )}
    </Box>
  );
}